import React from 'react';
import {LEAGUES} from '../../../constants/leagues';
import {getPlayerBonusConfig} from '../../../configs/playerBonus';
import {getVisibleRankedBotBonusType} from '../../../configs/visibleRankedBots';
import {getBotSupplySizeBonusForLeague} from '../../../configs/botLeagueSupplyGrowth';
import {getVisibleBotIconPath} from '../../../utils/bots/getVisibleBotIconPath';
import {LEAGUE_ADJECTIVES} from '../../../utils/bots/getVisibleBotTitle';
import {getBotSupplySizeBonusLines} from '../../../utils/bots/getBotSupplySizeBonusLines';
import {getBotDiceBonusLines} from '../../../utils/bots/getBotDiceBonusLines';
import {pluralizeRu} from '../../../utils/other/pluralizeRu';

export const NO_GROWTH_BONUS_TEXT = 'Бонуса к росту отрядов нет.';

export const formatWinPoints = (winPoints) => `+${winPoints} ${pluralizeRu(winPoints, 'очко', 'очка', 'очков')} победы`;

// Бонус бота лиги в рейтинговом матче — тот же конфиг, по которому бот
// получает его в матче (VISIBLE_RANKED_BOT_LEAGUE_RULES → PLAYER_BONUS_CONFIG)
export const getLeagueBonus = (leagueId) => getPlayerBonusConfig(getVisibleRankedBotBonusType(leagueId));

/**
 * Экономический бонус бота лиги и очки победы к доходу строками. Числа — из
 * того же конфига, по которому бот получает бонус в матче, поэтому текст
 * правил не разойдется с игрой.
 */
const getLeagueEconomyLines = (leagueId) => {
  const bonus = getLeagueBonus(leagueId);
  const lines = [];
  if (bonus.startingCoinsBonus > 0) {
    const coins = bonus.startingCoinsBonus;
    lines.push(`+${coins} ${pluralizeRu(coins, 'стартовая монета', 'стартовые монеты', 'стартовых монет')}`);
  }
  if (bonus.coinsIncomeBonus > 0) {
    const coins = bonus.coinsIncomeBonus;
    lines.push(`+${coins} ${pluralizeRu(coins, 'монета', 'монеты', 'монет')} к доходу`);
  }
  if (bonus.coinsIncomePerCityBonus > 0) {
    const coins = bonus.coinsIncomePerCityBonus;
    lines.push(`+${coins} ${pluralizeRu(coins, 'монета', 'монеты', 'монет')} к доходу за каждый город`);
  }
  if (bonus.constructionActionsBonus > 0) {
    const actions = bonus.constructionActionsBonus;
    lines.push(`+${actions} ${pluralizeRu(actions, 'действие', 'действия', 'действий')} в фазе Строительства`);
  }
  if (bonus.winPointsIncomePerCityBonus > 0) {
    lines.push(`${formatWinPoints(bonus.winPointsIncomePerCityBonus)} к доходу за каждый город`);
  }
  return lines;
};

/**
 * Прибавка бота лиги к росту отрядов строками — из того же конфига, по
 * которому бот получает ее в матче (configs/botLeagueSupplyGrowth). Пустой
 * массив — прибавки нет.
 */
export const getLeagueGrowthLines = (leagueId) => getBotSupplySizeBonusLines(getBotSupplySizeBonusForLeague(leagueId));

// Подкрутка костей бота лиги строками — по полу d6 его уровня бонуса (см.
// getBotD6MinRoll). Пустой массив — кости честные
const getLeagueDiceLines = (leagueId) => getBotDiceBonusLines(getLeagueBonus(leagueId).d6MinRoll);

/**
 * Бонусы явного бота каждой лиги — подзаголовок со значком бота и список.
 * Общий для разделов правил «Боты» и «Рейтинг».
 */
const BotLeagueBonuses = () => (
  <>
    {Object.values(LEAGUES).map((leagueId) => {
      const economyLines = getLeagueEconomyLines(leagueId);
      const growthLines = getLeagueGrowthLines(leagueId);
      const diceLines = getLeagueDiceLines(leagueId);
      return (
        <React.Fragment key={leagueId}>
          <h3>
            <img
              className={'rules-page_stage-icon'}
              src={getVisibleBotIconPath(leagueId, true)}
              alt={''}
              aria-hidden={'true'}
            />
            {LEAGUE_ADJECTIVES[leagueId]} бот
          </h3>
          <ul>
            {economyLines.length > 0
              ? economyLines.map((line) => <li key={line}>{line}</li>)
              : <li>Экономических бонусов нет.</li>}
            {growthLines.length > 0
              ? growthLines.map((line) => <li key={line}>{line}</li>)
              : <li>{NO_GROWTH_BONUS_TEXT}</li>}
            {diceLines.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </React.Fragment>
      );
    })}
  </>
);

export default BotLeagueBonuses;
